<template>
  <div class="hoistingHotWork">
    <Collapse
      v-for="item in hoistings"
      :key="item.id"
      :ref="`hoistingDetail_collapse_${item.id}`"
      class="hoistingDetail_collapse"
      :hoistingData="item"
      :keepContentAlive="true"
    >
      <CollapseTitleSlot
        :id="item.id"
        slot="title"
        :permitData="item"
        :title="hoistingLabel"
        :showStatus="false"
        :dotOption="dotOption"
        :showDot="showDot(item)"
      />
      <HoistingDetail
        slot="content"
        :key="item.id"
        :ref="`HoistingDetail_${item.id}`"
        :hoistingData="item"
        :isEdit="isEdit"
        :floorsOptions="floorsOptions"
        :isProjectAvailable="isProjectAvailable"
      />
    </Collapse>
    <template v-if="showHoistingTab && !disableContractorEdit">
      <div
        v-if="isEdit"
        class="addNewHoisting"
        @click="_addNewHoisting"
      >
        {{ addHoistingLabel }}
      </div>
      <div class="workPermit_hint">
        {{ $t(`termsAndConditions.${regionName}.workPermit.hintForEdinburghTower`, [hoistingLabel, 5]) }}
      </div>
      <div class="workPermit_hint">
        {{ $t(`termsAndConditions.${regionName}.workPermit.hintForOthers`, [hoistingLabel, 3]) }}
      </div>
      <div class="workPermit_hint">
        {{ $t(`termsAndConditions.${regionName}.workPermit.hintForPermitNumberLimit`, [ maxWAPPRHoistingNum, standalone? $t('workPermit.HoistingLimitHintType.work') : $t('workPermit.HoistingLimitHintType.project'), hoistingLabel ]) }}
      </div>
    </template>
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="delConfModalId"
      :confirmTitle="hoistingConfirmHint"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { computed, getCurrentInstance } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import Collapse from '@/controls/Collapse'
import mutationTypes from '@/store/mutation-types'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getKey } from '@/api/common/request'
import EventBus from '@/utils/eventBus'
import { getMaxWAPPRHoistingNum } from '@/api/workPermit/request'
import { WORK_PERMIT_STATUS, DEFAULT_MAX_WAPPR_HOISTING_NUM } from './consts'
import HoistingDetail from './HoistingDetail'
import CollapseTitleSlot from './CollapseTitleSlot'
import { disableTenantContractorEdit, showHoistingByModuleNHoisting } from './utils'
import { getAddHoistingLabel, getHoistingConfirmHint, getRegionalWorkPermitLabel } from './regionalWorkPermitLabels'

export default {
  name: 'HoistingHotWork',
  components: {
    HoistingDetail,
    Collapse,
    CollapseTitleSlot,
    ActionConfirmModal,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    isProjectAvailable: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const showHoistingTab = computed(() => {
      return showHoistingByModuleNHoisting(proxy.hoisting)
    })

    return {
      showHoistingTab,
    }
  },
  data () {
    return {
      hoistingsExpandMap: {},
      delConfModalId: `hoistingDelConf-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      maxWAPPRHoistingNum: DEFAULT_MAX_WAPPR_HOISTING_NUM,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit', 'buildingFloorZoneMap']),
    ...mapGetters('regions', ['regionName']),
    dotOption () {
      return [{
        title: this.$t('action.delete'),
        operate: this.openDeleteConfirm,
      }]
    },
    hoistings () {
      const result = lodash.get(this.currentWorkPermit, 'hoistings', [])
      result.forEach(hoisting => {
        if (this.hoistingsExpandMap[hoisting.id] === undefined) {
          this.hoistingsExpandMap[hoisting.id] = true
        }
      })
      return result
    },
    floorsOptions () {
      return this.currentWorkPermit.floors || []
    },
    standalone () {
      return this.currentWorkPermit && this.currentWorkPermit.standalone
    },
    hoistingLabel () {
      return getRegionalWorkPermitLabel('hoisting')
    },
    addHoistingLabel () {
      return getAddHoistingLabel()
    },
    hoistingConfirmHint () {
      return getHoistingConfirmHint(this.$t('action.delete'))
    },
    disableContractorEdit () {
      return disableTenantContractorEdit()
    },
  },
  async mounted () {
    EventBus.$on('controls-show-notice-expand-status', (id, status) => {
      this.$set(this.hoistingsExpandMap, id, status)
    })
    this.maxWAPPRHoistingNum = await getMaxWAPPRHoistingNum()
  },
  destroyed () {
    EventBus.$off('controls-show-notice-expand-status')
  },
  methods: {
    ...mapActions('workPermit', ['deleteHoistingLocalAndServer']),
    ...mapMutations('workPermit', {
      addNewHoisting: mutationTypes.WORKPERMIT_ADD_NEW_HOISTING,
      deleteHoistingLocal: mutationTypes.WORKPERMIT_DELETE_HOISTING_LOCAL,
    }),
    async _addNewHoisting () {
      const id = await getKey()
      const contractorId = lodash.get(this.currentWorkPermit, 'contractor.id')
      this.addNewHoisting({ id, isTenantProject: this.currentWorkPermit.isTenantProject, contractorId })
      setTimeout(() => {
        this.scrollTo(id)
      }, 10)
    },
    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.hoistingHotWork')
      const validationResults = this.hoistings.map(item => {
        const valid = this.validateHoisting(item)
        if (!valid) this.expandHoisting(item)
        return valid
      })
      const allValid = validationResults.every(valid => Boolean(valid))
      return fusionInputCheckUtil.isAllFieldsValid('.hoistingHotWork') && allValid
    },
    validateHoisting (item) {
      const hoistingDetailContent = lodash.get(this.$refs, [`HoistingDetail_${item.id}`, 0])
      return hoistingDetailContent && hoistingDetailContent.validate()
    },
    expandHoisting (item) {
      const hoistingCollapse = lodash.get(this.$refs, [`hoistingDetail_collapse_${item.id}`, 0])
      if (hoistingCollapse) hoistingCollapse.isExpand = true
    },
    showDot (item) {
      return (item.status === WORK_PERMIT_STATUS.DRAFT || item.status === WORK_PERMIT_STATUS.REJECTED) && !disableTenantContractorEdit()
    },
    openDeleteConfirm (hoisting) {
      this.$refs.deleteConfirmModal.open(this._deleteHoisting, [hoisting])
    },
    async _deleteHoisting (hoisting) {
      hoisting.createdAt ? await this.deleteHoistingLocalAndServer(hoisting) : this.deleteHoistingLocal(hoisting.id)

      delete this.hoistingsExpandMap[hoisting.id]
    },
    scrollTo (id) {
      const element = this.$refs[`HoistingDetail_${id}`]
      if (element && element.length > 0) {
        window.scrollTo(0, element[0].$el.offsetTop - 60)
        EventBus.emit('window_scroll')
      }
    },
  },
}
</script>

<style lang='scss'>
.hoistingHotWork {
  .addNewHoisting {
    width: 230px;
    border-radius: 20px;
    color: white;
    background: #6e7a91;
    height: 40px;
    line-height: 40px;
    font-weight: bold;
    text-align: center;
    margin: 20px auto;
    cursor: pointer;
    margin-bottom: 0;
  }
  .hoistingDetail_collapse {
    margin-bottom: 20px;
    & > .content {
      padding: 20px;
    }
  }
  .workPermit_hint {
    margin-top: 10px;
    text-align: center;
    color:#E49405;
  }
}
</style>
